import React from 'react';

/**
 * Signed anomaly gauge — needle sweeps from "below" (left) to "above" (right)
 * with 0 at top-center. Scaled to ±abs(bound). Compact form factor designed
 * to sit next to OutlookGauge in the Executive Summary row.
 */

const GAUGE_W = 140;
const GAUGE_H = 88;
const CENTER_X = GAUGE_W / 2;
const CENTER_Y = GAUGE_H - 8;
const RADIUS = 56;

type Palette = 'precip' | 'temperature';

function anomalyToAngle(value: number, bound: number): number {
  const clamped = Math.max(-bound, Math.min(bound, value));
  // Map -bound → π (left), 0 → π/2 (top), +bound → 0 (right)
  return Math.PI * (1 - (clamped + bound) / (2 * bound));
}

function arcPath(startAngle: number, endAngle: number, r: number): string {
  const x1 = CENTER_X + r * Math.cos(Math.PI - startAngle);
  const y1 = CENTER_Y - r * Math.sin(Math.PI - startAngle);
  const x2 = CENTER_X + r * Math.cos(Math.PI - endAngle);
  const y2 = CENTER_Y - r * Math.sin(Math.PI - endAngle);
  const largeArc = endAngle - startAngle > Math.PI ? 1 : 0;
  return `M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2}`;
}

type BandSpec = {startFrac: number; endFrac: number; color: string};

// Precip: dry (brown) on left → near-normal (gray) → wet (blue) on right
const PRECIP_BANDS: BandSpec[] = [
  {startFrac: 0, endFrac: 0.35, color: '#a16207'}, // amber-700
  {startFrac: 0.35, endFrac: 0.65, color: '#9ca3af'}, // gray-400
  {startFrac: 0.65, endFrac: 1, color: '#2563eb'}, // blue-600
];

// Temperature: cool (blue) on left → near-normal (gray) → warm (red) on right
const TEMP_BANDS: BandSpec[] = [
  {startFrac: 0, endFrac: 0.35, color: '#2563eb'}, // blue-600
  {startFrac: 0.35, endFrac: 0.65, color: '#9ca3af'}, // gray-400
  {startFrac: 0.65, endFrac: 1, color: '#dc2626'}, // red-600
];

function bandsFor(p: Palette): BandSpec[] {
  return p === 'precip' ? PRECIP_BANDS : TEMP_BANDS;
}

function directionText(value: number, palette: Palette): string {
  if (Math.abs(value) < 1e-3) return 'near climatology';
  if (palette === 'precip') return value > 0 ? 'wetter than climatology' : 'drier than climatology';
  return value > 0 ? 'warmer than climatology' : 'cooler than climatology';
}

type Props = {
  value: number;
  bound: number; // ±bound defines the gauge range
  unit: string;
  label: string;
  palette: Palette;
};

export default function AnomalyGauge({value, bound, unit, label, palette}: Props) {
  const angle = anomalyToAngle(value, bound);
  const needleLen = RADIUS - 6;
  const needleX = CENTER_X + needleLen * Math.cos(angle);
  const needleY = CENTER_Y - needleLen * Math.sin(angle);

  return (
    <div className="flex flex-col items-center">
      <svg width={GAUGE_W} height={GAUGE_H} viewBox={`0 0 ${GAUGE_W} ${GAUGE_H}`}>
        {bandsFor(palette).map((band, i) => (
          <path
            key={i}
            d={arcPath(band.startFrac * Math.PI, band.endFrac * Math.PI, RADIUS)}
            fill="none"
            stroke={band.color}
            strokeWidth={10}
            strokeLinecap="butt"
            opacity={0.7}
          />
        ))}
        <line
          x1={CENTER_X}
          y1={CENTER_Y}
          x2={needleX}
          y2={needleY}
          stroke="#1f2937"
          strokeWidth={2}
          strokeLinecap="round"
        />
        <circle cx={CENTER_X} cy={CENTER_Y} r={3} fill="#1f2937" />
      </svg>
      <div className="text-center -mt-1 leading-tight">
        <div className="text-sm font-semibold tabular-nums">
          {value > 0 ? '+' : ''}
          {value.toFixed(palette === 'temperature' ? 1 : 2)} {unit}
        </div>
        <div className="text-[10px] text-gray-500 uppercase tracking-wide">{label}</div>
        <div className="text-[10px] text-gray-500 italic">{directionText(value, palette)}</div>
      </div>
    </div>
  );
}
